import classNames from 'classnames';
import { equals, path, pathOr } from 'ramda';
import { useMemo } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import '../../../../../../css/graph/map-element.less';
import { toggleNoteVisibility as toggleNoteVisibilityAction } from '../../../../../js/actions/reduxActions/canvasNotes';
import {
    setContextMenuData as setContextMenuDataAction,
    setDraggingData as setDraggingDataAction,
    setHoveringMapElementID as setHoveringMapElementIDAction,
} from '../../../../../js/actions/reduxActions/graphEditor';
import { getElementStyles } from '../../../../../js/components/graph/elements/elementStyles';
import { sharedGraphElementProps } from '../../../../../js/components/graph/elements/graphElementUtils';
import NoteElement from '../../../../../js/components/graph/elements/map/notes/NoteElement';
import { useGraph } from '../../../../../js/components/graph/GraphProvider';
import { getVariableStyles, isColorFillMode } from '../../../../../js/components/graph/utils';
import { GRAPH_ELEMENT_TYPES, MAP_ELEMENT_TYPES } from '../../../../../ts/constants/graph';
import type { GroupElementAPI } from '../../../../sources/graph';
import type {
    CanvasNote,
    Dragging,
    ElementStyles,
    GraphEditorStore,
    MapElement as MapElementType,
    SubflowResponse,
    UseGraphForMapGroupElements,
} from '../../../../types/graph';
import { canAddOutcome } from '../../../../utils/mapElementOutcomes';
import { safeToLower } from '../../../../utils/string';
import { getTenantId } from '../../../../utils/tenant';
import { useNotificationDispatch } from '../../../notifications/NotificationProvider';
import { useUserSettings } from '../../UserSettingsProvider';
import MapElementBase from './MapElementBase';
import StandardElement from './StandardElement';
import WaitDescription from './wait/WaitDescription';

interface HasXYMapElement extends MapElementType {
    x: number;
    y: number;
}

interface NotesStore {
    canvasNotes: CanvasNote[];
}

interface Props
    extends Pick<
            GraphEditorStore,
            'hoveringMapElementId' | 'dragging' | 'searchQueries' | 'contextMenu'
        >,
        NotesStore {
    hoveringMapElementId: string;
    setHoveringMapElementID: (id: string | null) => void;
    dragging: Dragging;
    setDraggingData: (data: Dragging) => void;
    zoomLevel: number;
    mapElement: MapElementType;
    doNotRender: boolean;
    openConfig: () => void;
    flowId: string;
    editingToken: string;
    openMetadataEditor: () => void;
    groupElements: GroupElementAPI[];
    zoomViewBox: () => void;
    graphElement: SVGElement;
    toggleNoteVisibility: () => void;
    setContextMenuData: () => void;
    isClone?: boolean | undefined;
}

const MapElement = (props: Props) => {
    const { hoveringMapElementId, setHoveringMapElementID, dragging, setDraggingData, zoomLevel } =
        props;
    let { mapElement, doNotRender } = props;

    const dispatchNotification = useNotificationDispatch();

    if (!mapElement) {
        mapElement = {
            id: '',
            elementType: '',
            developerName: '',
        };
        doNotRender = true;
    }
    const { id, elementType, developerName } = mapElement;

    const userSettings = useUserSettings();
    const { canvasSettings } = userSettings;

    const navigate = useNavigate();

    const tenantId = getTenantId();

    const styles = useMemo(
        () =>
            getVariableStyles(
                getElementStyles(elementType),
                canvasSettings,
                zoomLevel,
            ) as ElementStyles,
        [elementType, canvasSettings, zoomLevel],
    );

    const ports = useMemo(() => {
        // Check if this map element can have more outcomes added
        const canDrag = canAddOutcome(mapElement);

        return [
            { style: styles.portLeft, key: 'left' },
            { style: styles.portRight, key: 'right' },
            { style: styles.portTop, key: 'up' },
            { style: styles.portBottom, key: 'down' },
        ].map(({ style, key }) => (
            // biome-ignore lint/a11y/noStaticElementInteractions: Requires dedicated refactor to use correct element
            <foreignObject
                data-testid={`port-${key}-${developerName as string}`}
                key={key}
                onMouseDown={(e) => {
                    if (!canDrag) {
                        // Prevent dragging if max outcomes reached
                        return;
                    }
                    e.stopPropagation();
                    setHoveringMapElementID(null);
                    setDraggingData({
                        dragType: GRAPH_ELEMENT_TYPES.outcome,
                        elementId: id,
                        previousMousePosition: { x: e.clientX, y: e.clientY },
                    });
                }}
                {...style.foreignobject}
                className="map-element-hover-wrapper"
                style={{ cursor: canDrag ? 'pointer' : 'not-allowed' }}
            >
                <style.icon
                    className="map-element-hover-icon"
                    weight="fill"
                    alt={canDrag ? 'Drag to create an outcome' : 'Maximum outcomes reached'}
                    style={{ opacity: canDrag ? 1 : 0.3 }}
                />
            </foreignObject>
        ));
    }, [
        id,
        developerName,
        mapElement,
        setDraggingData,
        setHoveringMapElementID,
        styles.portBottom,
        styles.portLeft,
        styles.portRight,
        styles.portTop,
    ]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to include all dependencies
    const actionText = useMemo(
        () =>
            [MAP_ELEMENT_TYPES.input, MAP_ELEMENT_TYPES.modal].includes(
                elementType.toLowerCase(),
            ) ? (
                <button
                    id={`${id}-hyper-link`}
                    className={classNames({
                        'map-element-description': true,
                        'link-emulate': true,
                        'color-white': isColorFillMode(
                            canvasSettings.mapElementColorStyle,
                            zoomLevel,
                        ),
                    })}
                    title={path(['page', 'developerName'], mapElement)}
                    onClick={() =>
                        navigate(`/${tenantId}/pages/${pathOr('', ['page', 'id'], mapElement)}`)
                    }
                    tabIndex={-1}
                    type="button"
                >
                    {path(['page', 'developerName'], mapElement)}
                </button>
            ) : equals(elementType.toLowerCase(), MAP_ELEMENT_TYPES.subflow) ? (
                <>
                    {(mapElement?.subflow as SubflowResponse).id && (
                        <button
                            id={`${id}-hyper-link`}
                            className={classNames({
                                'map-element-description': true,
                                'link-emulate': true,
                                'color-white': isColorFillMode(
                                    canvasSettings.mapElementColorStyle,
                                    zoomLevel,
                                ),
                            })}
                            title={path(['subflow', 'developerName'], mapElement)}
                            onClick={() =>
                                navigate(
                                    `/${tenantId}/flows/${pathOr(
                                        '',
                                        ['subflow', 'id'],
                                        mapElement,
                                    )}`,
                                )
                            }
                            tabIndex={-1}
                            type="button"
                        >
                            {path(['subflow', 'developerName'], mapElement)}
                        </button>
                    )}
                    {(mapElement?.subflow as SubflowResponse).isSetAsValue && (
                        <div
                            className={classNames({
                                'color-white': isColorFillMode(
                                    canvasSettings.mapElementColorStyle,
                                    zoomLevel,
                                ),
                            })}
                        >
                            Set as Value
                        </div>
                    )}
                </>
            ) : equals(elementType.toLowerCase(), MAP_ELEMENT_TYPES.wait) ? (
                <WaitDescription
                    wait={mapElement?.wait}
                    className={classNames({
                        'color-white': isColorFillMode(
                            canvasSettings.mapElementColorStyle,
                            zoomLevel,
                        ),
                    })}
                />
            ) : null,
        [canvasSettings.mapElementColorStyle, tenantId, elementType, id, mapElement, zoomLevel],
    );

    const useGraphData: UseGraphForMapGroupElements = useGraph();

    if (doNotRender) {
        return null;
    }

    return (
        <MapElementBase
            // Passing components in so that any element can inherit the base dragging functionality
            component={
                safeToLower(mapElement.elementType) === MAP_ELEMENT_TYPES.note
                    ? NoteElement
                    : StandardElement
            }
            mapElement={mapElement}
            styles={styles}
            dragging={dragging}
            {...sharedGraphElementProps({
                ...props,
                ...useGraphData,
                ...(mapElement as HasXYMapElement),
                element: mapElement,
                dragType: GRAPH_ELEMENT_TYPES.map,
                hoveringElementId: hoveringMapElementId,
                setHoveringElementID: setHoveringMapElementID,
                developerName: mapElement.developerName,
                onError: (error: unknown) => {
                    dispatchNotification({
                        type: 'notifyError',
                        payload: { error },
                    });
                },
            })}
            ports={ports}
            actionText={actionText}
            isColorFillMode={isColorFillMode(canvasSettings.mapElementColorStyle, zoomLevel)}
            isClone={props.isClone}
        />
    );
};

export default connect(
    (
        {
            graphEditor: { hoveringMapElementId, dragging, searchQueries, contextMenu },
            canvasNotes,
        }: { graphEditor: GraphEditorStore; canvasNotes: CanvasNote[] },
        ownProps,
    ) => ({
        ...ownProps,
        hoveringMapElementId,
        dragging,
        canvasNotes,
        searchQueries,
        contextMenu,
    }),
    {
        setHoveringMapElementID: setHoveringMapElementIDAction,
        setDraggingData: setDraggingDataAction,
        toggleNoteVisibility: toggleNoteVisibilityAction,
        setContextMenuData: setContextMenuDataAction,
    },
)(MapElement);
